import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  ArrowPathIcon,
  ChevronDownIcon,
  CheckIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const PreferencesSection = ({ user, organization, userRole, toastStyle, darkMode }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [availableModels, setAvailableModels] = useState([]);
  const [organizationSettings, setOrganizationSettings] = useState({
    availableProviders: {}
  });
  const [savedOrganizationSettings, setSavedOrganizationSettings] = useState({
    availableProviders: {}
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [expandedProviders, setExpandedProviders] = useState(() => {
    const savedState = localStorage.getItem('preferences-expanded-providers');
    return savedState ? JSON.parse(savedState) : { aiProviders: true };
  });
  // Check if user can manage preferences (admin or owner)
  const canManagePreferences = userRole === 'admin' || userRole === 'owner';

  useEffect(() => {
    fetchModelsAndSettings();
  }, []);

  // Check for changes whenever organization settings change
  useEffect(() => {
    const orgChanged = JSON.stringify(organizationSettings) !== JSON.stringify(savedOrganizationSettings);
    setHasChanges(orgChanged);
  }, [organizationSettings, savedOrganizationSettings]);

  const fetchModelsAndSettings = async () => {
    try {
      setIsLoading(true);
      
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No auth token found');
        return;
      }
      
      // Fetch ALL available models (regardless of organization preferences)
      let modelsResponse;
      try {
        // Use the regular models endpoint - it will return all enabled models
        // Organization filtering happens on the backend based on user's org
        modelsResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/models`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      } catch (error) {
        // Fallback to default endpoint if authenticated endpoint fails
        console.log('Falling back to default models endpoint');
        try {
          modelsResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/models/default`
          );
        } catch (fallbackError) {
          console.error('Error fetching models:', fallbackError);
          // Don't redirect on error - just log it
          modelsResponse = { data: [] };
        }
      }
      
      if (Array.isArray(modelsResponse.data) && modelsResponse.data.length > 0) {
        // Filter to only show models that are enabled by the master admin
        const enabledModels = modelsResponse.data.filter(model => model.enabled === true);
        setAvailableModels(enabledModels);
      }
      
      // Fetch organization settings
      try {
        const settingsResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/organization/preferences`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        if (settingsResponse.data.preferences) {
          // Ensure availableProviders exists
          const preferences = {
            ...settingsResponse.data.preferences,
            availableProviders: settingsResponse.data.preferences.availableProviders || {}
          };
          setOrganizationSettings(preferences);
          setSavedOrganizationSettings(preferences);
        }
      } catch (error) {
        // If no preferences exist yet, that's okay - we'll start with defaults
        if (error.response?.status !== 404) {
          console.error('Error fetching organization preferences:', error);
        }
      }
      
    } catch (error) {
      console.error('Error fetching models and settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProviderToggle = (provider) => {
    if (!canManagePreferences) return;
    
    setOrganizationSettings(prev => ({
      ...prev,
      availableProviders: {
        ...(prev.availableProviders || {}),
        [provider]: !(prev.availableProviders?.[provider] ?? false)
      }
    }));
  };

  const toggleProviderExpansion = (provider) => {
    setExpandedProviders(prev => {
      const newState = {
        ...prev,
        [provider]: !prev[provider]
      };
      // Save to localStorage
      localStorage.setItem('preferences-expanded-providers', JSON.stringify(newState));
      return newState;
    });
  };

  const expandAllProviders = () => {
    const allExpanded = { aiProviders: true };
    setExpandedProviders(allExpanded);
    localStorage.setItem('preferences-expanded-providers', JSON.stringify(allExpanded));
  };

  const collapseAllProviders = () => {
    const allCollapsed = { aiProviders: false };
    setExpandedProviders(allCollapsed);
    localStorage.setItem('preferences-expanded-providers', JSON.stringify(allCollapsed));
  };

  const areAllExpanded = expandedProviders.aiProviders;

  const handleSavePreferences = async () => {
    try {
      setSaving(true);
      
      const token = localStorage.getItem('token');
      
      await axios.put(
        `${process.env.REACT_APP_API_URL}/organization/preferences`,
        { preferences: organizationSettings },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setSavedOrganizationSettings(organizationSettings);
      setHasChanges(false);
      
    } catch (error) {
      console.error('Error saving preferences:', error);
    } finally {
      setSaving(false);
    }
  };

  const isProviderEnabled = (provider) => {
    // Default to enabled if not explicitly disabled
    // Safely check if availableProviders exists
    if (!organizationSettings?.availableProviders) {
      return true; // Default to enabled
    }
    return organizationSettings.availableProviders[provider] !== false;
  };

  const formatModelName = (modelName, modelData) => {
    // Handle special case for auto
    if (modelName === 'auto') {
      return 'Auto';
    }
    
    // If a custom display name is provided by the backend, use it
    if (modelData && modelData.displayName) {
      return modelData.displayName;
    }
    
    if (!modelName) return '';
    
    // If it's already a nice name like "Claude 3 Opus", return it as is
    if (/^[A-Z]/.test(modelName) || modelName.includes(' ')) {
      return modelName;
    }
    
    // Format based on model prefix
    if (modelName.startsWith('gpt-')) {
      return modelName
        .replace('gpt-', 'GPT-')
        .replace(/-([0-9])/g, ' $1')
        .replace(/-([a-z])/g, (_, letter) => ' ' + letter.toUpperCase());
    } else if (modelName.startsWith('claude-')) {
      return modelName
        .replace('claude-', 'Claude ')
        .replace(/-([0-9])/g, ' $1')
        .replace(/-([a-z])/g, ' $1')
        .replace(/\b([a-z])/g, letter => letter.toUpperCase())
        .replace(/-?\d{8}$/, ''); // Remove date suffixes
    } else if (modelName.startsWith('gemini-')) {
      return modelName
        .replace('gemini-', 'Gemini ')
        .replace(/-([0-9])/g, ' $1')
        .replace(/-([a-z])/g, ' $1')
        .replace(/\b([a-z])/g, letter => letter.toUpperCase());
    }
    
    // Generic formatting for other models
    return modelName
      .replace(/-([0-9])/g, ' $1')
      .replace(/-([a-z])/g, ' $1')
      .replace(/\b([a-z])/g, letter => letter.toUpperCase());
  };

  // Group models by provider
  const groupedModels = availableModels.reduce((acc, model) => {
    const provider = model.provider || 'unknown';
    if (!acc[provider]) {
      acc[provider] = [];
    }
    acc[provider].push(model);
    return acc;
  }, {});

  // Get provider display name
  const getProviderDisplayName = (provider) => {
    const lowerProvider = provider.toLowerCase();
    
    switch (lowerProvider) {
      case 'openai':
        return (
          <>
            OpenAI <span className="opacity-50">(ChatGPT)</span>
          </>
        );
      case 'anthropic':
        return (
          <>
            Anthropic <span className="opacity-50">(Claude)</span>
          </>
        );
      case 'google':
        return (
          <>
            Google <span className="opacity-50">(Gemini)</span>
          </>
        );
      default:
        return provider.charAt(0).toUpperCase() + provider.slice(1);
    }
  };

  if (isLoading) {
    return (
      <div className="preferences-section space-y-6 mt-2 rounded-2xl max-h-[calc(100vh-120px)] overflow-y-auto scrollbar-thin">
        <SkeletonTheme baseColor={darkMode ? "#202020" : "#e6e6e6"} highlightColor={darkMode ? "#333" : "#d8d8d8"}>
          {/* AI Providers section skeleton */}
          <div className="border border-gray-200 dark:border-[#282828] rounded-xl  p-4 space-y-3">
            <div className="flex items-center justify-between">
              <Skeleton height={20} width={150} />
              <Skeleton height={16} width={16} />
            </div>
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center justify-between">
                  <Skeleton height={16} width={120} />
                  <Skeleton height={16} width={16} />
                </div>
              ))}
            </div>
          </div>
          
          {/* Sign-On Security section skeleton */}
          <div className="border border-gray-200 dark:border-[#282828] rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <Skeleton height={20} width={120} />
              <Skeleton height={16} width={16} />
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex-1 space-y-1">
                  <Skeleton height={16} width={180} />
                  <Skeleton height={12} width={300} />
                </div>
                <Skeleton height={16} width={16} />
              </div>
              <div className="space-y-2">
                <Skeleton height={12} width={250} />
                <Skeleton height={12} width={200} />
              </div>
            </div>
          </div>
        </SkeletonTheme>
      </div>
    );
  }

  return (
    <div className="preferences-section">
      {/* Expand All Button - Fixed, not scrollable */}
      <div className="flex justify-end mb-4">
        <button
          onClick={areAllExpanded ? collapseAllProviders : expandAllProviders}
          className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
        >
          {areAllExpanded ? 'Collapse All' : 'Expand All'}
        </button>
      </div>

      {/* Scrollable Container */}
      <div className="max-h-[calc(100vh-200px)] overflow-y-auto scrollbar-thin" style={{ scrollbarWidth: 'thin', scrollbarColor: darkMode ? 'rgba(255, 255, 255, 0.1) transparent' : 'rgba(0, 0, 0, 0.2) transparent' }}>
        {/* Available AI Providers */}
        <div className="space-y-2 mb-4">
        {Object.keys(groupedModels).length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-gray-500 dark:text-gray-400">No AI providers available</p>
          </div>
        ) : (
          <>
            {/* Available AI Providers Container */}
            <div className="border border-gray-200 dark:border-[#282828] rounded-xl">
              <button
                onClick={() => toggleProviderExpansion('aiProviders')}
                className="w-full flex items-center justify-between p-4 text-left transition-colors"
              >
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  Available AI Providers
                </span>
                <ChevronDownIcon className={`w-4 h-4 text-gray-500 transition-transform ${
                  expandedProviders['aiProviders'] ? 'rotate-180' : ''
                }`} />
              </button>
              
              {expandedProviders['aiProviders'] && (
                <div className="px-4">
                  <div className="border-t border-gray-200 dark:border-[#282828] py-4 space-y-3">
                    {Object.entries(groupedModels).map(([provider, models]) => {
                      const providerEnabled = isProviderEnabled(provider);
                      
                      return (
                        <div key={provider} className="flex items-center justify-between">
                          <span className="text-sm text-gray-900 dark:text-white">
                            {getProviderDisplayName(provider)}
                          </span>
                                                     {canManagePreferences ? (
                             <button
                               onClick={() => handleProviderToggle(provider)}
                               className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                                 providerEnabled
                                   ? 'bg-black border-black dark:bg-white dark:border-white'
                                   : 'bg-transparent border-gray-400 dark:border-gray-500 hover:border-gray-500 dark:hover:border-gray-400'
                               }`}
                             >
                               {providerEnabled && (
                                 <CheckIcon className={`w-3 h-3 stroke-[4px] ${
                                   providerEnabled ? 'text-white dark:text-black' : ''
                                 }`} />
                               )}
                             </button>
                           ) : (
                             <div
                               className={`w-4 h-4 rounded border-2 flex items-center justify-center opacity-50 ${
                                 providerEnabled
                                   ? 'bg-gray-400 border-gray-400 dark:bg-gray-500 dark:border-gray-500'
                                   : 'bg-transparent border-gray-400 dark:border-gray-500'
                               }`}
                             >
                               {providerEnabled && (
                                 <CheckIcon className="w-3 h-3 stroke-2 text-white dark:text-black" />
                               )}
                             </div>
                           )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

          </>
        )}
      </div>

        {!canManagePreferences && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
            <div className="flex items-center">
              <InformationCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2" />
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Only organization admins and owners can modify these preferences.
              </p>
            </div>
          </div>
        )}

        {/* Save Button - Bottom Right */}
        {hasChanges && canManagePreferences && (
          <div className="flex justify-end mt-6">
            <button
              onClick={handleSavePreferences}
              disabled={saving}
              className="px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-lg hover:bg-gray-800 dark:hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center"
            >
              {saving ? (
                <>
                  <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PreferencesSection; 